import { supabase } from '../../lib/supabase'

/**
 * docs/product/scheduling-model.md's "Details": an employee's detail out of
 * their team for a date range, read from muster.details and created through
 * v1-create-details (20260925020000_details.sql). Status (active or not) is
 * never stored; currentDetailFor reads it from the dates, so it cannot
 * disagree with them.
 */
export type PersonnelDetail = {
  employeeId: string
  personName: string
  type: string
  originatingTeam: string
  originatingTeamId: string
  destination: string
  startDate: string
  endDate: string
}

type DetailRow = {
  employee_id: string
  detail_type: string
  originating_team_id: string
  destination: string
  start_date: string
  end_date: string
  teams: { name: string } | null
  employees: { display_name: string } | null
}

// Every detail the signed-in account can read: a supervisor's own teams',
// an administrator's every team's (details_read_team), oldest first.
export async function getPersonnelDetails(): Promise<PersonnelDetail[]> {
  const { data, error } = await supabase
    .from('details')
    .select('employee_id, detail_type, originating_team_id, destination, start_date, end_date, teams(name), employees(display_name)')
    .order('start_date', { ascending: true })
  if (error || !data) return []
  return (data as unknown as DetailRow[]).flatMap((row) =>
    row.employees
      ? [
          {
            employeeId: row.employee_id,
            personName: row.employees.display_name,
            type: row.detail_type,
            originatingTeam: row.teams?.name ?? '',
            originatingTeamId: row.originating_team_id,
            destination: row.destination,
            startDate: row.start_date,
            endDate: row.end_date,
          },
        ]
      : [],
  )
}

export type CreateDetailsParams = {
  employeeIds: string[]
  detailType: string
  destination: string
  startDate: string
  endDate: string
}

// The only path to create a detail: v1-create-details checks the caller
// supervises each employee's team, then writes one detail per employee, in
// one batch, in one transaction. Returns whether it landed, the same as
// every other write in this workspace.
export async function createDetails(params: CreateDetailsParams): Promise<boolean> {
  const { error } = await supabase.functions.invoke('v1-create-details', { body: params })
  return !error
}

// The detail effective for `today`, not just the last one outright: one
// whose own startDate has not arrived yet, or whose endDate has already
// passed, does not mark an employee Detailed.
export function currentDetailFor(details: PersonnelDetail[], employeeId: string, today: string): PersonnelDetail | undefined {
  const matches = details.filter(
    (detail) => detail.employeeId === employeeId && detail.startDate <= today && today <= detail.endDate,
  )
  return matches[matches.length - 1]
}
